import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../core/api/client';
import { crumb } from '../../core/telemetry/session';
import { doneLocally } from '../../core/offline/doneLocal';
import { isTerminal } from '../../core/domain/stateMachine';
import { stopStep, stopEnd } from '../../core/telemetry/stopLog';

/*
 * A face check that could not be asked for, remembered until it can be.
 *
 * Kept per day: an unanswered check from yesterday is not worth interrupting
 * today's first stop for, and the day's own checks will cover it.
 */
const OWED_KEY = 'jw.identityOwed';
/* When no fresh fix comes at Complete (31 Oct): a trail point this recent is
   preferred, and no fallback may be older than this. Server limit is 300 s. */
const FALLBACK_FRESH_MS = 120_000;
const FALLBACK_MAX_MS = 270_000;
const today = () => new Date().toDateString();
function identityOwed(): boolean {
  try { return localStorage.getItem(OWED_KEY) === today(); } catch { return false; }
}
function clearIdentityOwed(): void {
  try { localStorage.removeItem(OWED_KEY); } catch { /* fine */ }
}
import type { Task } from '../../core/domain/types';
import type { GeoFix } from '../../platform/types';
import { geolocation } from '../../platform/geolocation';
import { routeTracker } from '../../core/tracking/tracker';
import { submitCompletion, ProofUnreadable, trySync } from '../../core/offline/syncManager';
import { scanNeeded, clearScanNeeded } from '../../core/offline/idscans';
import { saveDraft, getDraft, clearDraft } from '../../core/offline/db';
import { PhotoCapture } from '../proof-capture/PhotoCapture';
import { SignaturePad } from '../proof-capture/SignaturePad';
import { BarcodeScanner, type BarcodeException } from '../proof-capture/BarcodeScanner';
import { IdentityCheck } from '../identity/IdentityCheck';
import { t, getLang } from '../../platform/lang';
import { reportError } from '../../core/telemetry/report';

/**
 * One stop, one question at a time.
 *
 * The owner's description, which is the whole specification: "when it starts
 * asking for the info, go one by one, just one click interaction per step. If it
 * asks for the name, right away the field for the name; as soon as I submit the
 * name, right away ask for signature; after submit ask for the next step. Once
 * all the steps are done the app should go back to the home screen so the driver
 * can repeat the process."
 *
 * What this replaces, twice over:
 *
 *   * a wizard that forced scan -> name -> signature -> photo in that order, so
 *     a driver holding a signed sheet but no name was stuck; and
 *   * a checklist hub that expected the driver to work out what to tap next and
 *     then scroll to find it.
 *
 * The screen now decides. Each step fills the middle of the display, takes one
 * action, and moves on by itself. Nothing is asked twice and nothing is left to
 * interpret. When the last piece is captured it submits and returns home — the
 * driver never presses "complete".
 *
 * These people are driving all day, most are not young, and they are holding a
 * tote while they do this. Every avoidable decision is a tax on that.
 */

type Step =
  | 'loading' | 'identity' | 'decide' | 'totes' | 'scan'
  | 'name' | 'signature' | 'photo' | 'switching' | 'sending' | 'problem' | 'already';

export function StopFlow() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<Task | null>(null);
  /*
   * WHICH check the server says is due. Hard-coding 'random_stop' here meant a
   * route-start check was filed under the wrong name, so the server never saw
   * one and asked again at every single stop — seven times in seven minutes.
   */
  const [idTrigger, setIdTrigger] = useState<'route_start' | 'random_stop'>('random_stop');
  const [step, setStep] = useState<Step>('loading');
  const [alreadyAt, setAlreadyAt] = useState<number | null>(null);

  /*
   * One line per screen the driver reaches, written where the step changes
   * rather than at each of the dozen places that change it -- so it cannot be
   * forgotten when a step is added.
   *
   * This is the trail that makes a silent death legible. A session whose last
   * crumbs read "totes > scan > scan > scan" and then simply stops was killed
   * with the camera open; one that ends at "signature" was not. Neither
   * produces an error, and without this neither leaves any trace at all.
   */
  useEffect(() => { crumb(`step:${step}`); stopStep(taskId, step); }, [step, taskId]);
  // The stop's own record goes when the driver leaves its screen (stopLog.ts).
  const ending = useRef('left');
  useEffect(() => () => stopEnd(taskId, ending.current), [taskId]);
  const [problem, setProblem] = useState<string | null>(null);
  /*
   * The machine-readable reason behind `problem`.
   *
   * A driver stood at the warehouse on 14 Sep with four totes and was refused
   * with "something went wrong", because the stop had never been started. There
   * was no way forward from that screen and she left three stops unfinished.
   * Keeping the reason lets the screen offer the one action that fixes it.
   */
  const [problemCode, setProblemCode] = useState<string | null>(null);

  const [totes, setTotes] = useState('');
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [barcodeException, setBarcodeException] = useState<BarcodeException | null>(null);
  /** Is the camera up right now? Closed after each tote — see the scan step. */
  const [scanOpen, setScanOpen] = useState(true);
  /*
   * There is deliberately no "you already scanned that" message.
   *
   * It existed as an orange warning and the owner had it removed: nothing is
   * wrong when it fires. The tote is counted, the driver is simply holding it
   * and has pointed the camera at it again, and the ticked list already shows
   * that it registered. Alarming somebody for doing nothing wrong teaches them
   * to ignore the warnings that do matter.
   *
   * The GUARD remains in the scan handler — a repeat is refused, so one tote
   * can never be recorded as five. It is now refused quietly.
   */
  /**
   * Totes handled that had nothing to scan — a plain cardboard box.
   *
   * Counted separately because there is no code to put in the list. The REASON
   * goes to the server in barcode_exception as it always has; this is only so
   * the driver can see the box ticked off and keep going.
   */
  const [noCodeTotes, setNoCodeTotes] = useState(0);
  /**
   * Codes already taken at this stop, in a ref rather than in state.
   *
   * THE REASON IS TIMING. The decoder keeps reading while the label is in
   * frame and hands us the same code again every two seconds. The guard used to
   * be `barcodes.includes(v)` — reading a STATE array captured in a closure, so
   * every one of those repeats saw the list as it was BEFORE the first scan,
   * decided the code was new, and appended it. Hold one tote steady for ten
   * seconds and the app records five totes, all with the same barcode.
   *
   * That is the worst kind of wrong: it does not look like a failure. It looks
   * like five totes were loaded, and it would have gone into the record as
   * five.
   *
   * A ref updates the instant it is written, so the second callback sees what
   * the first one did regardless of renders or batching.
   */
  const takenCodes = useRef<Set<string>>(new Set());
  const [recipient, setRecipient] = useState('');
  const [signature, setSignature] = useState<Blob | null>(null);
  const [photo, setPhoto] = useState<Blob | null>(null);

  // Guards a double submit when the last capture completes: the photo callback
  // and a re-render must not both fire the send.
  const sending = useRef(false);
  // The delivery is queued and only its face scan is missing (134).
  const [resendAfterScan, setResendAfterScan] = useState(false);
  const [idLive, setIdLive] = useState(false);

  const isPickup = task?.facility.facilityType === 'warehouse';
  /** Every tote dealt with at this stop, scanned or marked as having no code. */
  const totesRecorded = barcodes.length + noCodeTotes;

  /* ------------------------------------------------------------- loading -- */
  const load = useCallback(async () => {
    /*
     * If this throws, the driver must be TOLD, not left on "Loading...".
     *
     * There was no catch here. In poor signal getTodayRoutes throws, the
     * promise rejected, `step` stayed 'loading' for ever, and the screen sat
     * there with no error, no retry and no way out. That is the freeze drivers
     * reported, and it is worst exactly where they said: bad reception.
     */
    /*
     * The stop the driver just tapped is in the copy they were looking at one
     * second ago. Asking the server for it again — and making them wait twelve
     * seconds for the answer, or fail outright in a basement — was work for
     * nothing. The network is only consulted if there is no copy at all.
     */
    let routes = await api.cachedRoutesToday().catch(() => null);

    try {
      if (!routes) ({ routes } = await api.getTodayRoutes());
    } catch (err) {
      reportError('request_timeout', 'stop', err);
      setProblem(t('cannotLoadStop'));
      setProblemCode('cannot_load');
      setStep('problem');
      return;
    }

    const found = routes.flatMap((r) => r.tasks).find((x) => x.id === taskId);
    if (!found) { navigate('/route', { replace: true }); return; }
    setTask(found);

    /*
     * ALREADY FINISHED? Then nothing can be entered here (10 Oct). The saved
     * route can be older than the delivery (Jesus Camargo, 9 Oct: Mercy
     * General's delivery entered on the UC Davis screen). Checked three ways:
     * the saved route, this phone's own confirmed list, and the office.
     */
    const local = doneLocally(found.id);
    if (isTerminal(found.status) || local) {
      setAlreadyAt(local ? local.at : null);
      setStep('already');
      return;
    }
    try {
      const st = await api.taskState(found.id);
      if (st && ['completed', 'skipped', 'cancelled', 'failed'].includes(st.status)) {
        setAlreadyAt(st.completedAt ? new Date(st.completedAt).getTime() : null);
        setStep('already');
        return;
      }
    } catch { /* no answer (no signal): the saved route and this phone's list decide */ }

    /*
     * THE DELIVERY IS ALREADY ON THE PHONE, WAITING FOR ITS FACE SCAN (134).
     * The office would not accept it without one. Straight to the scan; the
     * delivery goes the moment it is taken. Nothing is captured twice.
     */
    if (scanNeeded(found.id)) {
      setIdTrigger(found.facility.facilityType === 'warehouse' ? 'route_start' : 'random_stop');
      setResendAfterScan(true);
      setStep('identity');
      return;
    }

    /*
     * Anything captured here before. A driver whose app crashed, or who took a
     * phone call and came back, resumes exactly where they were instead of
     * rescanning nine totes.
     */
    const draft = await getDraft(found.id);
    if (draft) {
      setTotes(draft.totes);
      setBarcodes(draft.barcodes);
      takenCodes.current = new Set(draft.barcodes);
      // Resuming with totes already scanned: show the list, not a camera. They
      // came back to finish, not to start again.
      setNoCodeTotes(draft.noCodeTotes ?? 0);
      if (draft.barcodes.length > 0 || (draft.noCodeTotes ?? 0) > 0) {
        setScanOpen(false);
      }
      setBarcodeException(draft.barcodeException as BarcodeException | null);
      setRecipient(draft.recipient);
      setSignature(draft.signature);
      setPhoto(draft.photo);
    }

    // The face check comes first when it is due, so it never interrupts a
    // capture half way through.
    try {
      const due = await api.identityDue(found.id);
      if (due.trigger === 'route_start' || due.trigger === 'random_stop') {
        setIdTrigger(due.trigger);
      }
      setIdLive(due.live === true);
      /*
       * A check we could not ask about earlier is COLLECTED HERE, at the first
       * stop where the office can be reached. See the catch below for why.
       */
      const owed = identityOwed();
      if (due.due || owed) {
        if (owed && !due.due) setIdTrigger('random_stop');
        /*
         * THE DEBT IS NOT CLEARED HERE. It is cleared by IdentityCheck, and
         * only once the office has actually recorded a scan.
         *
         * It used to be cleared the moment this screen was shown, which meant
         * a driver still out of signal at the next stop lost the debt for the
         * day: the upload failed, the check finished silently, and nothing was
         * ever recorded. On 22 Sep a driver was asked at 20:03 with no signal,
         * completed two more stops, and was never asked again on a ten-stop
         * route. A postponed check that forgets itself is not a postponement.
         */
        setStep('identity');
      } else {
        setStep('decide');
      }
    } catch (err) {
      /*
       * A face check we cannot ask about is not a reason to block the stop —
       * that rule stands, and a driver in a basement must keep working.
       *
       * But it used to pass in COMPLETE SILENCE, and that is the one way around
       * the identity check that exists: a phone deliberately put into airplane
       * mode cannot be asked, so it is never checked, and afterwards nothing
       * anywhere records that a check was skipped. The deliveries queue up and
       * arrive later looking perfectly normal.
       *
       * It still does not block. It is now simply ON THE RECORD, so a driver
       * whose checks are repeatedly "unavailable" is visible instead of
       * invisible. Held locally and sent when signal returns — which it must,
       * because the deliveries themselves have to reach us the same way.
       */
      /*
       * ...and it is now OWED. The check is not cancelled, it is postponed to
       * the first stop where we can reach the office.
       *
       * Without this, a phone with no signal is a driver with no identity
       * check, and the gap closes itself silently when signal returns. Two of
       * these happened on 18 Sep alone, both after genuine network drops. The
       * owner's rule is that software faults must not stop a driver working --
       * it is not that a check missed is a check forgiven.
       */
      /*
       * ...and since 17 Oct it is TAKEN HERE, not postponed. Owner: "face scan
       * cannot be skipped in any way." With the face scan at every stop on, a
       * postponed check was a lost one (Jesus, 15-16 Oct: 3 stops opened with
       * no signal had no scan). No answer from the office now means "scan":
       * the photo is kept on the phone and sent when signal returns.
       */
      reportError('face_check_unavailable', 'stop', `task ${found.id}`);
      setIdTrigger(found.facility.facilityType === 'warehouse' ? 'route_start' : 'random_stop');
      setStep('identity');
    }
  }, [taskId, navigate]);

  useEffect(() => { void load(); }, [load]);

  /*
   * ── THE MOMENT THE DRIVER OPENED THIS STOP ───────────────────────────────
   *
   * Arrival and completion were the only two timestamps a stop had, so every
   * minute between them was one undivided lump: driving into the car park,
   * waiting at a pharmacy window, and the app itself all looked identical.
   * This is the point that splits them.
   *
   * Rules it must obey, all of them for the same reason — a measurement is
   * never worth a second of a driver's time:
   *   - the LAST KNOWN fix only. Never getFreshFix: that can take twelve
   *     seconds, and the tracker is already running.
   *   - not awaited, and the failure is swallowed. The server records only
   *     the first open per task, so a re-entered stop is harmless.
   *   - keyed on task.id, not taskId, so it fires after the stop has actually
   *     loaded rather than while the id is still a URL parameter.
   */
  const openedSent = useRef<string | null>(null);
  useEffect(() => {
    if (!task) return;
    if (openedSent.current === task.id) return;
    openedSent.current = task.id;
    const fix = geolocation.getLastKnownFix(120_000);
    void api.stopOpened(task.id, fix?.lat ?? null, fix?.lng ?? null,
                        fix?.accuracyM ?? null).catch(() => { /* measurement only */ });
  }, [task]);

  /* --------------------------------------------------- the running order -- */
  /**
   * What comes next, worked out from what is already captured.
   *
   * Deliberately a function of state rather than a fixed list: a driver who
   * came back to a half-finished stop resumes where they stopped, and the order
   * can change for a pickup without a second code path.
   */
  const nextStep = useCallback((): Step => {
    if (isPickup && totes.trim() === '') return 'totes';
    // A pickup with 0 totes coming back has nothing to scan (10 Oct).
    if (barcodes.length === 0 && barcodeException === null
        && !(isPickup && totes.trim() === '0')) return 'scan';
    if (recipient.trim() === '') return 'name';
    if (signature === null) return 'signature';
    return 'photo';
  }, [isPickup, totes, barcodes.length, barcodeException, recipient, signature]);

  // 'decide' means "work out what is still missing"; resolving it in one place
  // is what lets a driver resume a half-finished stop where they left it.
  useEffect(() => {
    if (step === 'decide') setStep(nextStep());
  }, [step, nextStep]);

  /*
   * Moving on, with the camera given time to come back.
   *
   * One camera, one holder. The scanner is unmounted by this state change, but
   * an older iPhone does not release the hardware immediately, and the next
   * step asking for it too soon froze the viewfinder completely. The pause is
   * only taken when a camera step is involved.
   */
  /*
   * Written as it is captured, not at the end. This is the difference between
   * a crash costing a driver ten seconds and costing them the whole stop.
   */
  useEffect(() => {
    if (!task) return;
    if (barcodes.length === 0 && recipient === '' && !signature && !photo
        && totes === '' && barcodeException === null) return;
    void saveDraft({
      taskId: task.id, totes, barcodes, barcodeException, noCodeTotes,
      recipient, signature, photo,
    });
  }, [task, totes, barcodes, barcodeException, noCodeTotes,
      recipient, signature, photo]);

  const advance = () => {
    const to = nextStep();
    const cameraToCamera = (step === 'scan' && to === 'photo');
    if (cameraToCamera) {
      setStep('switching');
      setTimeout(() => setStep(to), 400);
      return;
    }
    setStep(to);
  };

  /* ------------------------------------------------------------- sending -- */
  const send = async (photo: Blob) => {
    if (sending.current || !task) return;
    sending.current = true;
    setStep('sending');
    try {
      /*
       * A completion is measured against where the driver is NOW. A reading
       * from the previous stop would be checked against the wrong building —
       * refusing an honest driver, or accepting a delivery that did not happen
       * where it says. 60 seconds is the most a fallback may be.
       */
      /*
       * Five minutes, not one. A driver parks, walks in, works the stop and
       * comes back -- the fix taken in the car park is the only one they will
       * get, and at 60 seconds it had already been discarded by the time they
       * reached the counter. The true age travels with it, so the server still
       * applies its own freshness rule; we simply stop throwing away the only
       * evidence we have.
       */
      let fix: GeoFix | null = null;
      let freshError: string | null = null;
      try { fix = await geolocation.getFreshFix(12_000); } catch (e) {
        /* keep going -- but remember why, for the report below */
        freshError = (e as Error)?.message ?? String(e);
      }
      /*
       * No fresh fix (31 Oct). The fallback is chosen AFTER the 12-second
       * wait, never before it, and never older than 270 s: the server refuses
       * anything over 300 s. Marryam Razzaq, Washington Hospital, 30 Oct: the
       * fallback was picked before the wait, was 302 s old when sent, and a
       * real delivery 90 m from the door was refused. A newer trail point
       * (58 s old) existed and was passed over for being less precise.
       */
      if (!fix) {
        fix = routeTracker.newestRecentFix(FALLBACK_FRESH_MS) ?? geolocation.getLastKnownFix(FALLBACK_MAX_MS);
      }

      /*
       * Prefer a more ACCURATE recent fix over a vaguer fresh one — but only if
       * it is also RECENT. A driver walking into a lobby gets worse readings
       * with every step, so the newest fix is often the least precise.
       *
       * THE AGE CAP IS THE WHOLE POINT. bestRecentFix() looks back fifteen
       * minutes, and the first version of this chose purely on accuracy: on
       * 15 Sep it handed the server a fix 722 seconds old, which was refused,
       * costing a retry for someone standing in exactly the right place. The
       * server rejects anything older than 300s, so 90s leaves generous room
       * and still only ever swaps in something genuinely current.
       */
      const FRESH_ENOUGH_MS = 90_000;
      const best = routeTracker.bestRecentFix(FRESH_ENOUGH_MS);
      if (best && (!fix || best.accuracyM < fix.accuracyM)) fix = best;
      stopStep(task.id, fix ? `pressed_complete:acc${Math.round(fix.accuracyM)}` : 'pressed_complete:nofix');
      if (!fix) {
        // The moment a driver describes as "the app can't tell where I am".
        reportError('no_location', 'stop', {
          error: freshError?.slice(0, 120) ?? null,
          online: navigator.onLine,
          visible: document.visibilityState,
        });
        setProblem(t('accuracyBody'));
        setProblemCode('rejected_accuracy');
        setStep('problem');
        sending.current = false;
        return;
      }

      /*
       * Queued first, then we wait only a SHORT while for the verdict.
       *
       * submitCompletion enqueues the delivery and then flushes it: a signed
       * URL, a photo, a signature, each allowed up to 45 seconds. Awaiting all
       * of that held drivers on "Sending..." for over a minute in weak signal,
       * for work that was already safe on the phone the moment it was queued.
       *
       * So: wait long enough to catch the common case -- a refusal for distance
       * or accuracy, which the driver can still fix while standing there -- and
       * otherwise go home and let the queue finish in its own time. Nothing is
       * lost either way; the only difference is who is kept waiting.
       */
      const sendPromise = submitCompletion({
        taskId: task.id,
        fix,
        photoBlob: photo,
        signatureBlob: signature,
        recipientName: recipient.trim() || null,
        barcodes,
        barcodeException,
        totesReturned: totes.trim() === '' ? null : Number(totes.trim()),
      });
      // Never let a rejection here surface as an unhandled error: the queue
      // owns retrying, and this promise may outlive the screen.
      sendPromise.catch(() => {});

      const WAIT_FOR_VERDICT_MS = 8_000;
      const result = await Promise.race([
        sendPromise,
        new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), WAIT_FOR_VERDICT_MS);
        }),
      ]);

      if (result === null) {
        // Still going. It is queued, so it will arrive; stop holding them here.
        ending.current = 'still_sending';
        await clearDraft(task.id);
        navigate('/route', { replace: true });
        return;
      }

      // No face scan reached the office (134): the delivery stays queued on
      // the phone, the scan is taken now, and then it is sent.
      if (result.needsScan?.includes(task.id)) {
        stopStep(task.id, 'refused:face_scan_required');
        await clearDraft(task.id);
        setIdTrigger(isPickup ? 'route_start' : 'random_stop');
        setResendAfterScan(true);
        setStep('identity');
        sending.current = false;
        return;
      }

      const refused = result.rejected.find((r) => r.taskId === task.id);
      if (refused) {
        stopStep(task.id, `refused:${refused.error}${refused.distanceM != null ? `:${Math.round(refused.distanceM)}m` : ''}`);
        setProblem(explain(refused));
        setProblemCode(refused.error);
        setStep('problem');
        sending.current = false;
        return;
      }

      /*
       * Straight home. No confirmation screen to dismiss: the stop disappearing
       * from the list IS the confirmation, and the owner was explicit that a
       * summary nobody reads should not sit in the way.
       */
      ending.current = result.authExpired ? 'sign_in_expired'
        : `answer:sent${result.synced}:waiting${result.remaining}`;
      await clearDraft(task.id);
      navigate('/route', { replace: true });
    } catch (err) {
      // The phone lost the picture before it could be saved: retake it here,
      // at the stop, instead of leaving with a delivery that can never send.
      if (err instanceof ProofUnreadable) {
        if (err.which === 'signature') setSignature(null); else setPhoto(null);
        setProblem(t(err.which === 'signature' ? 'retakeSignature' : 'retakePhoto'));
        setProblemCode('proof_unreadable');
        setStep('problem');
        sending.current = false;
        return;
      }
      reportError('upload_failed', 'stop', err);
      ending.current = `error:${String((err as Error)?.message ?? err).slice(0, 60)}`;
      // Queued offline. The driver is finished either way.
      navigate('/route', { replace: true });
    }
  };

  const explain = (
    r: { error: string; distanceM?: number; radiusM?: number; stale_fix?: boolean },
  ): string => {
    const here = task?.facility.name ?? '';
    if (r.stale_fix) return t('staleFix');
    switch (r.error) {
      case 'rejected_distance':
        return r.distanceM !== undefined && r.radiusM !== undefined
          ? t('farWithLimit', {
              ft: Math.round(r.distanceM * 3.28084), name: here,
              limit: Math.round(r.radiusM * 3.28084) })
          : t('farUnknown', { name: here });
      /*
       * The stop was never started, so the server has nothing to complete. Not
       * the driver's mistake to work out — the screen now offers the button.
       */
      case 'rejected_state': return t('notStartedBody', { name: here });
      case 'rejected_accuracy': return t('accuracyBody');
      case 'rejected_missing_proof': return t('proofMissingBody');
      case 'proof_lost': return t('proofLostBody', { name: here });
      default: return t('refusedBody', { name: here });
    }
  };

  /* --------------------------------------------------------------- views -- */
  /*
   * The stop could not be loaded. Rendered BEFORE the loading guard below,
   * because on this path `task` is still null and that guard would send the
   * driver straight back to "Loading..." — the very freeze this exists to end.
   *
   * No header here for the same reason: it reads task.facility.name.
   */
  if (step === 'already') {
    const es = getLang() === 'es';
    const when = alreadyAt ? new Date(alreadyAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
    return (
      <main className="screen">
        <div className="empty-state">
          <h1>{es ? 'Esta parada ya está cerrada' : 'This stop is already done'}</h1>
          {task && <p><b>{task.facility.name}</b></p>}
          <div className="banner warn-banner">
            {es ? `La oficina ya tiene esta parada cerrada${when ? ` (${when})` : ''}. No hay nada más que hacer aquí.`
                : `The office already has this stop as done${when ? ` (${when})` : ''}. There is nothing more to do here.`}
            <br />
            {es ? 'Si está en otro hospital, regrese y abra ESE hospital.'
                : 'If you are at a different hospital, go back and open THAT hospital.'}
          </div>
          <button className="btn primary big" onClick={() => navigate('/route', { replace: true })}>{t('back')}</button>
        </div>
      </main>
    );
  }

  if (step === 'problem' && !task) {
    return (
      <main className="screen">
        <div className="empty-state">
          <h1>{t('cannotLoadTitle')}</h1>
          <div className="banner warn-banner">{problem}</div>
          <button className="btn primary big" onClick={() => {
            setProblem(null);
            setProblemCode(null);
            setStep('loading');
            void load();
          }}>{t('tryAgain')}</button>
          <div className="empty-actions">
            <button className="btn secondary"
                    onClick={() => navigate('/route')}>{t('back')}</button>
          </div>
        </div>
      </main>
    );
  }

  if (step === 'loading' || !task) {
    return <main className="screen"><p>{t('loading')}</p></main>;
  }

  if (step === 'identity') {
    return (
      <IdentityCheck trigger={idTrigger} taskId={task.id} live={idLive}
                     onRecorded={clearIdentityOwed}
                     onFinished={() => {
                       if (!resendAfterScan) { setStep(nextStep()); return; }
                       clearScanNeeded(task.id);
                       void trySync();
                       navigate('/route', { replace: true });
                     }} />
    );
  }

  /*
   * The step BEFORE this one, so a mistake can be corrected without abandoning
   * the stop. Null on the first screen, where back means leaving.
   */
  const previousStep = (): Step | null => {
    const order: Step[] = isPickup
      ? ['totes', 'scan', 'name', 'signature', 'photo']
      : ['scan', 'name', 'signature', 'photo'];
    const at = order.indexOf(step);
    return at > 0 ? (order[at - 1] ?? null) : null;
  };

  const header = (
    <header className="flow-head">
      <button className="btn link" onClick={() => navigate('/route')}>{t('back')}</button>
      <p className="flow-where">{task.facility.name}</p>
    </header>
  );

  /*
   * Correcting a mistake. A driver mistyped something and had no way
   * back: the only exit was leaving the stop altogether, which reads as losing
   * the work. This goes back exactly one screen with her answer still in it.
   *
   * Nothing is cleared. The draft holds every answer, so the step she returns
   * to shows what she gave and she simply edits it.
   */
  const changeLast = (() => {
    const prev = previousStep();
    if (!prev) return null;
    return (
      <button className="btn link back-a-step"
              onClick={() => { sending.current = false; setStep(prev); }}>
        {t('changeLast')}
      </button>
    );
  })();

  if (step === 'totes') {
    const n = totes.trim();
    const ok = n !== '' && Number.isInteger(Number(n)) && Number(n) >= 0 && Number(n) <= 500;
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body">
          <h1>{t('totesTitle')}</h1>
          <p className="hint">{t('totesBody')}</p>
          <input className="input big-input" type="number" inputMode="numeric"
                 autoFocus min={0} max={500} step={1} value={totes}
                 onChange={(e) => setTotes(e.target.value)} />
        </div>
        <button className="btn primary big" disabled={!ok} onClick={advance}>
          {t('next')}
        </button>
        {changeLast}
      </main>
    );
  }

  if (step === 'scan') {
    /*
     * One tote at a time, and the camera CLOSES after each one.
     *
     * It used to stay open and keep decoding, with a running count underneath.
     * That failed in the field for three separate reasons, all reported in one
     * test: a second tote carrying the same label was silently dropped as a
     * duplicate; a pause longer than the camera liked never picked up again;
     * and nothing on screen told the driver whether it was still listening.
     *
     * So the scan is now a deliberate act with a visible result. Each tote gets
     * a ticked line on a list — the list the owner asked for, because a person
     * carrying six totes needs to see SIX ticks, not the number six — and the
     * next one needs a button press. Slower by one tap, and certain.
     */
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body">
          <h1>{isPickup ? t('listTotesOut') : t('listScan')}</h1>

          {totesRecorded > 0 && (
            <ol className="tote-checklist">
              {barcodes.map((code, i) => (
                <li key={code} className="tote-done">
                  <span className="tote-tick" aria-hidden="true">✓</span>
                  <span className="tote-n">{t('toteN', { n: i + 1 })}</span>
                  <code className="tote-code">{code}</code>
                  {/*
                    * Take one back off the list.
                    *
                    * A driver scanned the wrong item on 20 Sep and there was no
                    * way to undo it -- the tote count and the barcode list
                    * were both wrong and he could only carry on. The office
                    * could reopen the whole stop afterwards, which is far more
                    * than the mistake deserves.
                    *
                    * This touches nothing but what is on the phone before the
                    * stop is sent: no server call, no change to a finished
                    * delivery, nothing that can reach the record. Removing the
                    * wrong one costs a rescan.
                    *
                    * `takenCodes` must be cleared too, or the same sticker can
                    * never be scanned again on this stop -- it is the guard
                    * that stops one label being counted twice.
                    */}
                  <button type="button" className="tote-remove"
                          aria-label={t('removeToteAria', { n: i + 1 })}
                          onClick={() => {
                            takenCodes.current.delete(code);
                            setBarcodes((b) => b.filter((c) => c !== code));
                          }}>
                    {t('removeTote')}
                  </button>
                </li>
              ))}
              {/* Boxes with nothing to scan. Ticked like the rest: they were
                  handled, and the driver should see them counted. */}
              {Array.from({ length: noCodeTotes }, (_, i) => (
                <li key={`nocode-${i}`} className="tote-done tote-nocode">
                  <span className="tote-tick" aria-hidden="true">✓</span>
                  <span className="tote-n">
                    {t('toteN', { n: barcodes.length + i + 1 })}
                  </span>
                  <span className="tote-code">{t('toteNoCode')}</span>
                  <button type="button" className="tote-remove"
                          aria-label={t('removeToteAria',
                                        { n: barcodes.length + i + 1 })}
                          onClick={() => setNoCodeTotes((n) => Math.max(0, n - 1))}>
                    {t('removeTote')}
                  </button>
                </li>
              ))}
            </ol>
          )}


          {scanOpen ? (
            <BarcodeScanner
              /*
               * NEVER opens by itself, and this is the owner's decision, not
               * mine. Scanning is easier than typing when it works; it does not
               * reliably work yet, and drivers report it taking too long or
               * doing nothing. Until that is fixed and proven, the camera is
               * something a driver chooses, not something that happens to them.
               *
               * Do not set this back to automatic without being asked.
               */
              autoStart={false}
              onScanned={(v, ex) => {
                /*
                 * "This one has no barcode" is a fact about the box, not a
                 * failure, and it used to jump straight to the next step — so a
                 * driver carrying five totes where only the third was a plain
                 * cardboard box could not record that and keep going. It now
                 * adds a line to the list exactly like a scan does, and the
                 * driver decides when they are finished.
                 */
                if (ex) {
                  setBarcodeException(ex);
                  setNoCodeTotes((n) => n + 1);
                  setScanOpen(false);
                  return;
                }
                if (!v) return;
                /*
                 * Say so rather than ignoring it. Two totes genuinely can carry
                 * the same label, and a driver who scans one and sees nothing
                 * happen has no way to tell that from a scanner that has died.
                 */
                // Synchronous, so a repeat two seconds later cannot slip past.
                // Quietly ignored: the list already shows it was counted.
                if (takenCodes.current.has(v)) return;
                takenCodes.current.add(v);
                setBarcodes((b) => (b.includes(v) ? b : [...b, v]));
                setScanOpen(false);      // unmounts the scanner: camera released
              }} />
          ) : (
            <p className="hint">{t('toteAddedHint')}</p>
          )}
        </div>

        {totesRecorded > 0 && !scanOpen && (
          <div className="flow-actions">
            <button className="btn secondary big"
                    onClick={() => setScanOpen(true)}>
              {t('toteScanAnother')}
            </button>
            <button className="btn primary big" onClick={advance}>
              {t('nextAfterScans', { n: totesRecorded })}
            </button>
            {changeLast}
          </div>
        )}
      </main>
    );
  }

  if (step === 'name') {
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body">
          <h1>{t(isPickup ? 'whoHandedOver' : 'whoReceived')}</h1>
          <input className="input big-input" autoFocus
                 placeholder={t('listName')}
                 value={recipient}
                 onChange={(e) => setRecipient(e.target.value)} />
        </div>
        <button className="btn primary big" disabled={!recipient.trim()}
                onClick={advance}>
          {t('next')}
        </button>
        {changeLast}
      </main>
    );
  }

  if (step === 'signature') {
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body">
          <h1>{t('listSign')}</h1>
          <SignaturePad
            atPickup={isPickup}
            onCancel={() => setStep('name')}
            onCapture={(b) => { setSignature(b); setStep('photo'); }} />
        </div>
      </main>
    );
  }

  if (step === 'photo') {
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body">
          <h1>{isPickup ? t('listManifest') : t('listPhoto')}</h1>
          <PhotoCapture onCancel={() => setStep('signature')}
                        onCapture={(b) => { setPhoto(b); void send(b); }} />
        </div>
      </main>
    );
  }

  if (step === 'switching') {
    // Never a blank screen: a driver looking at nothing assumes it has crashed,
    // which is what they had just learned to expect.
    return (
      <main className="screen flow">
        {header}
        <div className="flow-body"><p className="sending">{t('oneMoment')}</p></div>
      </main>
    );
  }

  if (step === 'sending') {
    return <main className="screen flow"><p className="sending">{t('sending')}</p></main>;
  }

  // Something was refused. Say what to do, and let them try the last step again
  // without losing anything they captured.
  return (
    <main className="screen flow">
      {header}
      <div className="flow-body">
        <div className="banner warn-banner">{problem}</div>
        {/*
          * A stop that was never started is one tap from being fixed, so offer
          * that tap rather than leaving the driver to work it out. Everything
          * they captured is still held; this starts the stop and resubmits.
          */}
        {problemCode === 'rejected_state' && (
          <button className="btn primary big" onClick={() => {
            void (async () => {
              setStep('sending');
              try {
                const fix = geolocation.getLastKnownFix(120_000);
                await api.beginStop(task.id, fix?.lat ?? null, fix?.lng ?? null);
              } catch (err) {
                // Offline: the completion still queues and will be accepted
                // once the start reaches the server.
                reportError('request_timeout', 'stop', err);
              }
              sending.current = false;
              setStep('photo');
            })();
          }}>
            {t('startThenRetry')}
          </button>
        )}
        <button className="btn primary big" onClick={() => {
          sending.current = false;
          setStep(problemCode === 'proof_unreadable' && !signature ? 'signature' : 'photo');
        }}>
          {t('tryAgain')}
        </button>
        <button className="btn link" onClick={() => navigate('/route')}>{t('back')}</button>
      </div>
    </main>
  );
}
